import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toast } from "../../../components/modern-ui/sonner";
import { AnimatePresence } from "framer-motion";
import { FiShoppingCart } from "react-icons/fi";

import { useSelector, useDispatch } from "react-redux";
import {
  selectCart,
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
} from "../../../store/slices/cart/slice";
import {
  selectOrders,
  clearCreateSuccess,
  clearOrdersError,
} from "../../../store/slices/orders/slice";
import { createOrder } from "../../../store/slices/orders/asyncThunks";
import useCartAvailabilityCheck from "../../../hooks/useCartAvailabilityCheck";

import MainContainer from "../../../components/utils/MainContainer";
import { Container, EmptyCart } from "./CartPage.styles";
import { DEFAULT_COUNTRY } from "../../../utils/constants";
import { trackPixel } from "../../../utils/metaPixel";
import { useNavigate, useSearchParams } from "react-router-dom";

import Stepper from "./components/Stepper";
import CartReviewStep from "./components/CartReviewStep";
import CheckoutStep from "./components/CheckoutStep";
import {
  ITEMS_PER_PAGE,
  computeTotals,
  createBaseOrderData,
  createOrderLabel,
  createProductsArray,
  getCountryConfig,
  groupItemsByShipping,
  loadFormData,
  saveFormData,
  scrollToTop,
  validateCheckoutForm,
} from "./helpers";

const STEP_CART = 1;
const STEP_CHECKOUT = 2;

const STEP_QUERY_KEY = "step";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  zipCode: "",
  details: "",
};

export default function CartPage() {
  const dispatch = useDispatch();

  // Ponovna provera pri ulasku u korpu — korisnik je mogao dugo da stoji na
  // sajtu otkad je provera odradjena na startu aplikacije.
  useCartAvailabilityCheck();

  const cart = useSelector(selectCart);
  const orders = useSelector(selectOrders);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepFromQuery = parseInt(searchParams.get(STEP_QUERY_KEY) || "1");
  const [step, setStep] = useState(
    stepFromQuery === STEP_CHECKOUT ? STEP_CHECKOUT : STEP_CART,
  );

  const centralPage = parseInt(searchParams.get("centralPage") || "1");

  const [formData, setFormData] = useState(initialFormData);
  const [deliveryMethod, setDeliveryMethod] = useState("delivery");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [errors, setErrors] = useState({});

  // FE-05: keš uspešno kreiranih order-grupa (idempotencija pri retry-u).
  // Ključ = sellerKey / "central"; vrednost = { signature, entry }.
  const createdGroupsRef = useRef(new Map());

  const countryConfig = getCountryConfig(country);

  useEffect(() => {
    const savedData = loadFormData();
    if (savedData) {
      setFormData((prev) => ({ ...prev, ...savedData }));
    }
  }, []);


  useEffect(() => {
    saveFormData(formData);
  }, [formData]);

  // keep step in sync with URL (so refresh / back-button work)
  useEffect(() => {
    const current = parseInt(searchParams.get(STEP_QUERY_KEY) || "1");
    if (current !== step) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set(STEP_QUERY_KEY, step);
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const itemsByShippingGroup = useMemo(
    () => groupItemsByShipping(cart.items),
    [cart.items],
  );

  const isPickupAvailable = useMemo(
    () =>
      itemsByShippingGroup.bandShipping.size === 0 &&
      countryConfig.pickupAllowed,
    [itemsByShippingGroup.bandShipping, countryConfig.pickupAllowed],
  );

  useEffect(() => {
    if (!isPickupAvailable && deliveryMethod === "pickup") {
      setDeliveryMethod("delivery");
    }
  }, [isPickupAvailable, deliveryMethod]);

  // if cart becomes empty while at checkout, fall back to step 1
  useEffect(() => {
    if (!cart.items?.length && step !== STEP_CART) {
      setStep(STEP_CART);
    }
  }, [cart.items?.length, step]);

  const centralTotalPages = Math.ceil(
    itemsByShippingGroup.centralShipping.length / ITEMS_PER_PAGE,
  );
  const centralStartIndex = (centralPage - 1) * ITEMS_PER_PAGE;
  const paginatedCentralItems = itemsByShippingGroup.centralShipping.slice(
    centralStartIndex,
    centralStartIndex + ITEMS_PER_PAGE,
  );

  const paginatedBandGroups = useMemo(() => {
    const result = new Map();
    itemsByShippingGroup.bandShipping.forEach((bandGroup, sellerKey) => {
      const totalPages = Math.ceil(bandGroup.items.length / ITEMS_PER_PAGE);
      const currentPageForGroup = parseInt(
        searchParams.get(`seller_${sellerKey}_page`) || "1",
      );
      const startIndex = (currentPageForGroup - 1) * ITEMS_PER_PAGE;
      const paginatedItems = bandGroup.items.slice(
        startIndex,
        startIndex + ITEMS_PER_PAGE,
      );

      result.set(sellerKey, {
        ...bandGroup,
        items: paginatedItems,
        currentPage: currentPageForGroup,
        totalPages,
        allItems: bandGroup.items,
      });
    });
    return result;
  }, [itemsByShippingGroup.bandShipping, searchParams]);

  const totals = useMemo(
    () =>
      computeTotals({
        itemsByShippingGroup,
        deliveryMethod,
        country,
      }),
    [itemsByShippingGroup, deliveryMethod, country],
  );

  // Ako brisanje isprazni tekuću stranu (npr. jedini proizvod na strani 2),
  // spusti se na poslednju postojeću stranu umesto prazne tabele; parametri
  // grupa koje više ne postoje se čiste iz URL-a
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let changed = false;

    const clampPage = (key, current, totalPages) => {
      const maxPage = Math.max(1, totalPages);
      if (current > maxPage) {
        if (maxPage <= 1) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(maxPage));
        }
        changed = true;
      }
    };

    clampPage("centralPage", centralPage, centralTotalPages);

    const activeSellerKeys = new Set();
    itemsByShippingGroup.bandShipping.forEach((bandGroup, sellerKey) => {
      activeSellerKeys.add(`seller_${sellerKey}_page`);
      const key = `seller_${sellerKey}_page`;
      const current = parseInt(searchParams.get(key) || "1");
      const totalPages = Math.ceil(bandGroup.items.length / ITEMS_PER_PAGE);
      clampPage(key, current, totalPages);
    });

    [...newParams.keys()].forEach((key) => {
      if (
        key.startsWith("seller_") &&
        key.endsWith("_page") &&
        !activeSellerKeys.has(key)
      ) {
        newParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      setSearchParams(newParams, { replace: true });
    }
  }, [
    itemsByShippingGroup,
    centralPage,
    centralTotalPages,
    searchParams,
    setSearchParams,
  ]);

  // v2 s.9.2: uklanjanje sa undo toastom ("Vrati", 5s)
  const removeWithUndo = useCallback(
    (cartItemId) => {
      const removedItem = cart.items?.find(
        (item) => item.cartItemId === cartItemId,
      );
      dispatch(removeFromCart(cartItemId));

      if (removedItem) {
        toast.custom("Proizvod uklonjen", {
          duration: 5000,
          action: {
            label: "Vrati",
            onClick: () =>
              dispatch(
                addToCart({
                  product: removedItem,
                  quantity: removedItem.quantity,
                  selectedSize: removedItem.selectedSize,
                }),
              ),
          },
        });
      }
    },
    [dispatch, cart.items],
  );

  const handleUpdateQuantity = useCallback(
    (cartItemId, newQuantity) => {
      if (newQuantity < 1) {
        removeWithUndo(cartItemId);
      } else {
        dispatch(updateQuantity({ cartItemId, quantity: newQuantity }));
      }
    },
    [dispatch, removeWithUndo],
  );

  const handleRemoveItem = removeWithUndo;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateCheckoutForm(formData, deliveryMethod);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // v2 s.13.5: skrol do prvog nevalidnog polja
      setTimeout(() => {
        const firstField = Object.keys(newErrors)[0];
        const node =
          document.getElementById(firstField) ||
          document.querySelector(`[data-field-error="${firstField}"]`);
        node?.scrollIntoView({ behavior: "smooth", block: "center" });
        node?.focus?.();
      }, 50);
      return;
    }
    setErrors({});
    dispatch(clearOrdersError());

    const baseOrderData = createBaseOrderData(
      formData,
      deliveryMethod,
      country,
    );

    try {
      const orderResults = [];
      const created = createdGroupsRef.current;

      // FE-05: potpis sadržaja grupe (id:kolicina:velicina), koristi se da
      // prepoznamo da li je ista grupa već uspešno kreirana u prethodnom pokušaju.
      const groupSignature = (items) =>
        JSON.stringify(
          items
            .map((it) => `${it.id}:${it.quantity}:${it.selectedSize || ""}`)
            .sort(),
        );

      for (const [
        sellerKey,
        bandGroup,
      ] of itemsByShippingGroup.bandShipping.entries()) {
        const signature = groupSignature(bandGroup.items);
        const prior = created.get(sellerKey);

        // FE-05: grupa je već uspešno kreirana (isti sadržaj) → ne šalji ponovo.
        if (prior && prior.signature === signature) {
          orderResults.push(prior.entry);
          continue;
        }

        const sellerOrderData = {
          ...baseOrderData,
          details: `${formData.details} ${createOrderLabel(bandGroup.sellerType, bandGroup.sellerName)}`,
          products: createProductsArray(bandGroup.items),
        };

        const sellerResult = await dispatch(
          createOrder(sellerOrderData),
        ).unwrap();

        const entry = {
          type: bandGroup.sellerType,
          sellerKey,
          sellerName: bandGroup.sellerName,
          sellerId: bandGroup.sellerId,
          bandName: bandGroup.bandName,
          bandId: bandGroup.bandId,
          festivalId: bandGroup.festivalId,
          result: sellerResult.data,
        };
        created.set(sellerKey, { signature, entry });
        orderResults.push(entry);
      }

      if (itemsByShippingGroup.centralShipping.length > 0) {
        const signature = groupSignature(itemsByShippingGroup.centralShipping);
        const prior = created.get("central");

        if (prior && prior.signature === signature) {
          orderResults.push(prior.entry);
        } else {
          const centralOrderData = {
            ...baseOrderData,
            details: `${formData.details} ${createOrderLabel("central")}`,
            products: createProductsArray(itemsByShippingGroup.centralShipping),
          };

          const centralResult = await dispatch(
            createOrder(centralOrderData),
          ).unwrap();

          const entry = { type: "central", result: centralResult.data };
          created.set("central", { signature, entry });
          orderResults.push(entry);
        }
      }

      // FE-05: sve grupe uspešno kreirane → resetuj idempotency keš.
      createdGroupsRef.current = new Map();

      const combinedTotal = orderResults.reduce(
        (sum, order) => sum + order.result.totalPrice,
        0,
      );
      const orderNumbers = orderResults
        .map((order) => order.result.orderNumber)
        .join(", ");

      /* Purchase se šalje tek ovde — sve grupe porudžbina su uspešno
         kreirane na serveru (neuspeh bilo koje baca u catch), a value
         je stvarni ukupan iznos iz odgovora servera. */
      trackPixel("Purchase", {
        content_ids: (cart.items || []).map((item) => String(item.id)),
        content_type: "product",
        num_items: cart.totalItems,
        value: combinedTotal,
        currency: "RSD",
      });

      const orderData = {
        orderNumber: orderNumbers,
        total: combinedTotal,
        items: cart.totalItems,
        // snapshot stavki za rekapitulaciju na potvrdi (v2 s.14.1)
        itemsList: cart.items,
        subtotal: totals.subtotal,
        shipping: totals.shipping,
        customerInfo: formData,
        deliveryMethod,
        country,
        countryName: countryConfig.name,
        hasMultipleOrders: orderResults.length > 1,
        orders: orderResults,
      };

      navigate("/porudzbina-uspesna", { state: { orderData } });

      setTimeout(() => {
        scrollToTop();
        dispatch(clearCart());
        dispatch(clearCreateSuccess());
      }, 300);
    } catch (error) {
      console.error("Order creation error:", error);
      toast.error(
        error?.message ||
          "Došlo je do greške prilikom kreiranja porudžbine. Pokušajte ponovo.",
      );
    }
  };

  const handleCentralPageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("centralPage", page);
    setSearchParams(newParams);
    scrollToTop();
  };

  const handleBandPageChange = (sellerKey, page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(`seller_${sellerKey}_page`, page);
    setSearchParams(newParams);
    scrollToTop();
  };

  const handleContinueToCheckout = () => {
    trackPixel("InitiateCheckout", {
      content_ids: (cart.items || []).map((item) => String(item.id)),
      content_type: "product",
      num_items: cart.totalItems,
      value: totals.total,
      currency: "RSD",
    });
    setStep(STEP_CHECKOUT);
    scrollToTop();
  };

  const handleBackToCart = () => {
    setStep(STEP_CART);
    scrollToTop();
  };

  const handleBackToShop = () => {
    navigate("/prodavnica");
  };

  const isCartEmpty = !cart.items?.length;

  return (
    <MainContainer>
      <Container>
        {isCartEmpty ? (
          <EmptyCart>
            <FiShoppingCart size={48} style={{ marginBottom: "0.5rem" }} />
            <h3>Korpa je prazna</h3>
            <p>Pogledaj šta ima novo u prodavnici.</p>
            <button
              onClick={() => navigate("/prodavnica")}
              style={{
                background: "var(--brand)",
                color: "#fff",
                border: "none",
                height: "50px",
                padding: "0 1.75rem",
                borderRadius: "var(--radius-control)",
                cursor: "pointer",
                marginTop: "1.25rem",
                fontSize: "0.98rem",
                fontWeight: 700,
                fontFamily: "var(--font-body)",
              }}
            >
              U prodavnicu
            </button>
          </EmptyCart>
        ) : (
          <>
            <Stepper
              currentStep={step}
              onStepClick={(stepId) => {
                if (stepId === STEP_CART) handleBackToCart();
              }}
            />

            <AnimatePresence mode="wait">
              {step === STEP_CART ? (
                <CartReviewStep
                  key="cart-review"
                  cart={cart}
                  itemsByShippingGroup={itemsByShippingGroup}
                  paginatedCentralItems={paginatedCentralItems}
                  centralPage={centralPage}
                  centralTotalPages={centralTotalPages}
                  paginatedBandGroups={paginatedBandGroups}
                  totals={totals}
                  country={country}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                  onCentralPageChange={handleCentralPageChange}
                  onBandPageChange={handleBandPageChange}
                  onContinue={handleContinueToCheckout}
                  onBackToShop={handleBackToShop}
                />
              ) : (
                <CheckoutStep
                  key="checkout"
                  formData={formData}
                  errors={errors}
                  deliveryMethod={deliveryMethod}
                  country={country}
                  isPickupAvailable={isPickupAvailable}
                  totals={totals}
                  items={cart.items}
                  hasItems={!isCartEmpty}
                  isSubmitting={orders.createLoading}
                  isSubmitSuccess={orders.createSuccess}
                  apiError={orders.error}
                  onInputChange={handleInputChange}
                  onCountryChange={setCountry}
                  onDeliveryMethodChange={setDeliveryMethod}
                  onSubmit={handleSubmit}
                  onBack={handleBackToCart}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </Container>
    </MainContainer>
  );
}
